import {
  ArrowUp,
  FileJson,
  FileText,
  Mic,
  MicOff,
  Sparkles,
  Volume2,
  Network,
  FolderOpen,
} from "@jurisui/core/icons";
import { Input } from "@jurisui/core/primitives/input";
import { Alert, AlertDescription } from "@jurisui/core/primitives/alert";
import { Badge } from "@jurisui/core/primitives/badge";
import { Button } from "@jurisui/core/primitives/button";
import { Checkbox } from "@jurisui/core/primitives/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@jurisui/core/primitives/dialog";
import { Label } from "@jurisui/core/primitives/label";
import { Textarea } from "@jurisui/core/primitives/textarea";
import { TooltipProvider } from "@jurisui/core/primitives/tooltip";
import { CompanionMessageItem } from "@jurisui/companion/react";
import { useSidebar } from "@jurisui/react/primitives/sidebar";
import { AppShell } from "@jurisui/react/components/layout/AppShell";
import { AppSidebarNav } from "@jurisui/react/components/layout/AppSidebarNav";
import { AppUserMenu } from "@jurisui/react/components/layout/AppUserMenu";
import { AppLauncher } from "@jurisui/react/components/layout/AppLauncher";
import type {
  CompanionConversationSummary,
  CompanionLaunchTarget,
  CompanionDiagramRecipe,
} from "@jurisui/companion/types";
import type { DiagramRecipe, DiagramFocus, DiagramKind } from "./diagram-types";
import {
  FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { MarkdownBlock } from "./components/MarkdownBlock";
import {
  preloadMermaid,
  ProgressiveMermaid,
} from "./components/ProgressiveMermaid";
import {
  FakeRealtimeBridge,
  JurisRealtimeBridge,
  type RealtimeBridge,
  type RealtimeCallbacks,
} from "./realtime";
import { createManoloRuntime } from "./manolo-runtime";
import { DiagramExplorer } from "./components/DiagramExplorer";
import { ConversationWorkspace } from "@jurisui/studio-ui";
import { RecipeDiagram } from "./components/RecipeDiagram";
import {
  ARCHITECTURE_PHASES,
  ARCHITECTURE_VIEWS,
  createArchitectureContext,
  normalizeArchitectureContext,
  type ArchitectureContext,
  type ArchitectureSnapshot,
  type ArchitecturePhase,
  type ArchitectureViewName,
} from "./architecture-model";
import "./architecture.css";
import {
  captureTurnScope,
  diagramMatchesSnapshot,
  scopeKey,
  SAFE_VOICE_FAILURE,
  type TurnOptions,
} from "./turn-scope";
import type {
  AppConfig,
  ConversationMessage,
  LiveDocumentSnapshotV1,
  TurnResponse,
} from "./types";

function LiveDocsNavigation({
  onNew,
  onSaved,
  onExplore,
}: {
  onNew(): void;
  onSaved(): void;
  onExplore(): void;
}) {
  const { setOpenMobile } = useSidebar();
  const select = (action: () => void) => () => {
    setOpenMobile(false);
    action();
  };
  return (
    <AppSidebarNav
      groups={[
        {
          label: "Live Docs",
          items: [
            {
              id: "new",
              label: "New chat",
              icon: FileText,
              onSelect: select(onNew),
            },
            {
              id: "saved",
              label: "Saved documents",
              icon: FolderOpen,
              onSelect: select(onSaved),
            },
            {
              id: "explore",
              label: "Explore architecture",
              icon: Network,
              onSelect: select(onExplore),
            },
          ],
        },
      ]}
    />
  );
}

const EMPTY_DOCUMENT: LiveDocumentSnapshotV1 = {
  schemaVersion: "juris.live-docs.document.v1",
  revision: 0,
  sections: [],
};

function downloadText(content: string, type: string, fileName: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function documentAsMarkdown(
  document: LiveDocumentSnapshotV1,
  profileName: string,
) {
  const lines = [
    `# Live Docs — ${profileName} session`,
    "",
    `Document revision: ${document.revision}`,
  ];
  for (const section of document.sections) {
    lines.push("", `## ${section.title}`);
    for (const block of section.blocks) {
      if (block.type === "markdown") lines.push("", block.markdown);
      else if (block.format === "mermaid")
        lines.push(
          "",
          `### ${block.title}`,
          "",
          block.description,
          "",
          "```mermaid",
          block.source,
          "```",
        );
      else
        lines.push(
          "",
          `### ${block.spec.title}`,
          "",
          block.spec.description,
          "",
          `Mode: ${block.spec.mode}. Catalogue: ${block.spec.catalog.revision} (${block.spec.catalog.phase}).`,
          "",
          "```json",
          JSON.stringify(block.spec, null, 2),
          "```",
        );
    }
  }
  return `${lines.join("\n")}\n`;
}

export interface LiveDocsAppProps {
  getAccessToken?: () => Promise<string | null>;
  account?: { name: string; email?: string; onSignOut(): Promise<void> };
}

const noAccessToken = async () => null;

export default function App({
  getAccessToken = noAccessToken,
  account,
}: LiveDocsAppProps) {
  const runtime = useMemo(
    () => createManoloRuntime(getAccessToken),
    [getAccessToken],
  );
  const [config, setConfig] = useState<AppConfig>();
  const [configurationError, setConfigurationError] = useState("");
  const [disclosureChecked, setDisclosureChecked] = useState(false);
  const [disclosureAccepted, setDisclosureAccepted] = useState(false);
  const [status, setStatus] = useState(
    "Preparing the canonical knowledge set…",
  );
  const [appSessionId, setAppSessionId] = useState<string>();
  const [documentState, setDocumentState] =
    useState<LiveDocumentSnapshotV1>(EMPTY_DOCUMENT);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceStarting, setVoiceStarting] = useState(false);
  const voiceStartingRef = useRef(false);
  const [remoteAudioAttached, setRemoteAudioAttached] = useState(false);
  const [error, setError] = useState("");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [splitTabId, setSplitTabId] = useState<string | null>(null);
  const layoutKey = `live-docs-layout:${Array.from(account?.email ?? "local").reduce((hash, character) => (hash * 31 + character.charCodeAt(0)) | 0, 0)}:${appSessionId ?? "draft"}`;
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(layoutKey) ?? "null");
      if (saved) {
        setWorkspaceOpen(saved.workspace === true);
        setSummaryOpen(saved.summary === true);
        setSplitTabId(typeof saved.split === "string" ? saved.split : null);
      }
    } catch {}
  }, [layoutKey]);
  useEffect(() => {
    try {
      localStorage.setItem(
        layoutKey,
        JSON.stringify({
          workspace: workspaceOpen,
          summary: summaryOpen,
          split: splitTabId,
        }),
      );
    } catch {}
  }, [workspaceOpen, summaryOpen, splitTabId]);
  const [openTabIds, setOpenTabIds] = useState<string[]>(["explore"]);
  const [focusLabel, setFocusLabel] = useState("");
  const [explorerEpoch, setExplorerEpoch] = useState(0);
  const activeTabRef = useRef<string>("explore");
  const focusedTabs = useRef(
    new Map<
      string,
      { focus: DiagramFocus; label: string; kind: DiagramKind; displayLevel?: "overview" | "technical" }
    >(),
  );
  const composerFocusRef = useRef<{ focus: DiagramFocus; kind: DiagramKind; displayLevel?: "overview" | "technical" }>();
  function activateTab(id: string) {
    activeTabRef.current = id;
    setActiveDiagramId(id);
    const focus = focusedTabs.current.get(id);
    composerFocusRef.current =
      focus &&
      (focus.focus.entityId || focus.focus.boundaryId || focus.focus.flowId || focus.displayLevel)
        ? focus
        : undefined;
    setFocusLabel(focus?.label === "Overview" ? "" : (focus?.label ?? ""));
    if (architectureRef.current)
      changeArchitectureContext({
        ...architectureRef.current,
        selectedEntityIds: focus?.focus.entityId ? [focus.focus.entityId] : [],
        selectedRelationshipIds: [],
      });
  }
  const [completeRevealToken, setCompleteRevealToken] = useState(0);

  const [architectureSnapshot, setArchitectureSnapshot] =
    useState<ArchitectureSnapshot>();
  const [architectureContext, setArchitectureContext] =
    useState<ArchitectureContext>();
  const [architectureError, setArchitectureError] = useState("");
  const [architectureLoading, setArchitectureLoading] = useState(false);
  const [highlightedEntityIds, setHighlightedEntityIds] = useState<string[]>(
    [],
  );
  const [citedSources, setCitedSources] = useState<
    ConversationMessage["sources"]
  >([]);
  const [launchTargets, setLaunchTargets] = useState<CompanionLaunchTarget[]>(
    [],
  );
  const [documentTitle, setDocumentTitle] = useState(
    "Untitled architecture document",
  );
  const [documentSaved, setDocumentSaved] = useState(false);
  const [transcriptOnly, setTranscriptOnly] = useState(false);
  const [documentDialog, setDocumentDialog] = useState<
    "open" | "save" | "rename" | "copy" | null
  >(null);
  const [documentNameInput, setDocumentNameInput] = useState("");
  const [savedDocuments, setSavedDocuments] = useState<
    CompanionConversationSummary[]
  >([]);
  const [documentOperationBusy, setDocumentOperationBusy] = useState(false);
  const [activeDiagramId, setActiveDiagramId] = useState<string>();
  const [answerDiagrams, setAnswerDiagrams] = useState<DiagramRecipe[]>([]);
  const [accountOpen, setAccountOpen] = useState(false);
  const [askMode, setAskMode] = useState<"documented" | "what-if">(
    "documented",
  );
  const architectureRef = useRef<ArchitectureContext>();
  const snapshotRef = useRef<ArchitectureSnapshot>();

  const [discussionDiagramId, setDiscussionDiagramId] = useState<string>();
  const discussionDiagramRef = useRef<DiagramRecipe>();
  const [voiceCaption, setVoiceCaption] = useState("");
  const contextEpochRef = useRef(0);
  const userCaptionIdRef = useRef<string>();
  const voiceCaptionAllowedRef = useRef(false);
  const delegatedCaptionIdsRef = useRef(new Set<string>());

  const sessionPromiseRef = useRef<Promise<string>>();
  const sessionEpochRef = useRef(0);
  const appSessionIdRef = useRef<string>();
  const documentRef = useRef(documentState);
  const messagesRef = useRef(messages);
  const requestEpochRef = useRef(0);
  const turnIntentRef = useRef(0);
  const voiceEpochRef = useRef(0);
  const turnQueueRef = useRef({
    sessionEpoch: 0,
    tail: Promise.resolve(),
    pending: 0,
  });
  const bridgeRef = useRef<RealtimeBridge>();
  const mountedRef = useRef(true);
  const consentRef = useRef<HTMLButtonElement>(null);
  const turnHandlerRef =
    useRef<
      (
        text: string,
        source: "text" | "voice",
        options?: TurnOptions,
      ) => Promise<void>
    >();
  const profileName = config?.profile ?? "the architecture assistant";
  const wakePhrase = config?.wakePhrase ?? "the configured wake phrase";

  const changeArchitectureContext = useCallback((next: ArchitectureContext) => {
    if (scopeKey(next) !== scopeKey(architectureRef.current)) {
      ++contextEpochRef.current;
      bridgeRef.current?.interrupt();
      voiceCaptionAllowedRef.current = false;
      setVoiceCaption("");
      userCaptionIdRef.current = undefined;
      discussionDiagramRef.current = undefined;
      setDiscussionDiagramId(undefined);
      setHighlightedEntityIds([]);
    }
    architectureRef.current = next;
    setArchitectureContext(next);
  }, []);
  useEffect(() => {
    snapshotRef.current = architectureSnapshot;
  }, [architectureSnapshot]);

  const loadArchitecture = useCallback(async () => {
    setArchitectureLoading(true);
    setArchitectureError("");
    try {
      const snapshot = await runtime.architecture();
      const context = createArchitectureContext(snapshot);
      setArchitectureSnapshot(snapshot);
      snapshotRef.current = snapshot;
      changeArchitectureContext(context);
      setHighlightedEntityIds([]);
      setError("");
    } catch (caught) {
      setArchitectureError(
        caught instanceof Error
          ? caught.message
          : "The architecture catalogue could not be loaded.",
      );
    } finally {
      setArchitectureLoading(false);
    }
  }, [runtime, changeArchitectureContext]);

  useEffect(() => {
    if (
      config &&
      !architectureSnapshot &&
      !architectureLoading &&
      !architectureError
    ) {
      void loadArchitecture();
    }
  }, [
    config,
    architectureSnapshot,
    architectureLoading,
    architectureError,
    loadArchitecture,
  ]);

  function sourceEntityIds(sources: ConversationMessage["sources"]) {
    const snapshot = snapshotRef.current;
    if (!snapshot) return [];
    const known = new Set(snapshot.catalog.entities.map((entity) => entity.id));
    return [
      ...new Set(
        (sources ?? []).flatMap((source) => {
          const metadata = source.metadata as
            Record<string, unknown> | undefined;
          if (
            !metadata ||
            metadata.catalogId !== snapshot.catalog.catalogId ||
            metadata.revision !== snapshot.catalog.revision ||
            metadata.sha256 !== snapshot.sha256
          )
            return [];
          return Array.isArray(metadata.entityIds)
            ? metadata.entityIds.filter(
                (id): id is string => typeof id === "string" && known.has(id),
              )
            : [];
        }),
      ),
    ];
  }

  function showArchitectureSources(sources: ConversationMessage["sources"]) {
    const ids = sourceEntityIds(sources);
    if (!ids.length || !architectureRef.current) return;
    setHighlightedEntityIds(ids);
    setCitedSources(sources);
    setOpenTabIds((entries) => [...new Set([...entries, "sources"])]);
    activateTab("sources");
    setWorkspaceOpen(true);
  }

  useEffect(() => {
    void preloadMermaid().catch(() => undefined);
  }, []);

  useEffect(() => {
    documentRef.current = documentState;
  }, [documentState]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    if (config && !disclosureAccepted) consentRef.current?.focus();
  }, [config, disclosureAccepted]);

  useEffect(() => {
    void runtime
      .config()
      .then((value) => {
        setConfig(value);
        setStatus(`Ready. Accept the disclosure to use ${value.profile}.`);
      })
      .catch((caught) => {
        setConfigurationError(
          caught instanceof Error
            ? caught.message
            : "The Juris configuration could not be loaded.",
        );
        setStatus("Configuration failed. Live Docs is disabled.");
      });
  }, [runtime]);

  useEffect(() => {
    if (config && !config.testMode)
      void runtime
        .launchTargets()
        .then(setLaunchTargets)
        .catch((caught) =>
          setError(
            caught instanceof Error
              ? caught.message
              : "Juris applications could not be loaded.",
          ),
        );
  }, [config, runtime]);

  const savedDiagrams = useMemo(
    () =>
      documentState.sections.flatMap((section) =>
        section.blocks.flatMap((block) =>
          block.type === "diagram" && block.format === "recipe"
            ? [block.spec as DiagramRecipe]
            : [],
        ),
      ),
    [documentState],
  );
  const allDiagrams = useMemo(
    () => [
      ...new Map(
        [...savedDiagrams, ...answerDiagrams].map((diagram) => [
          diagram.id,
          diagram,
        ]),
      ).values(),
    ],
    [savedDiagrams, answerDiagrams],
  );

  const addMessage = useCallback((message: ConversationMessage) => {
    setMessages((current) => {
      if (current.some((item) => item.id === message.id)) return current;
      return [...current, message];
    });
  }, []);

  const updateVoiceCaption = useCallback(
    (role: "user" | "assistant", text: string, messageId?: string) => {
      if (!mountedRef.current || !text.trim()) return;
      // Native and commentary captions are transient. The Control Plane turn is
      // the single authoritative assistant answer in the conversation.
      if (role === "assistant") {
        if (voiceCaptionAllowedRef.current) setVoiceCaption(text);
        return;
      }
      const id =
        messageId ??
        userCaptionIdRef.current ??
        `voice-user-${crypto.randomUUID()}`;
      voiceCaptionAllowedRef.current = !delegatedCaptionIdsRef.current.has(id);
      setVoiceCaption("");
      userCaptionIdRef.current = id;
      setMessages((current) => {
        const existing = current.findIndex((message) => message.id === id);
        if (existing === -1) return [...current, { id, role, content: text }];
        return current.map((message, index) =>
          index === existing ? { ...message, content: text } : message,
        );
      });
    },
    [],
  );

  const finishCurrentOutput = useCallback(() => {
    setCompleteRevealToken((value) => value + 1);
  }, []);

  const releaseRealtimeSession = useCallback(async () => {
    const currentSession = appSessionIdRef.current;
    if (!currentSession) return;
    await runtime.releaseRealtimeSession();
  }, [runtime]);

  const ensureAppSession = useCallback(async () => {
    if (appSessionIdRef.current) return appSessionIdRef.current;
    if (sessionPromiseRef.current) return sessionPromiseRef.current;
    if (!disclosureAccepted)
      throw new Error("Accept the disclosure before starting a session.");

    const sessionEpoch = sessionEpochRef.current;
    sessionPromiseRef.current = runtime
      .createSession()
      .then((body) => {
        if (sessionEpoch !== sessionEpochRef.current)
          throw new Error("The document session changed while opening.");
        appSessionIdRef.current = body.appSessionId;
        setAppSessionId(body.appSessionId);
        setDocumentState(body.document);
        return body.appSessionId as string;
      })
      .finally(() => {
        if (sessionEpoch === sessionEpochRef.current)
          sessionPromiseRef.current = undefined;
      });
    return sessionPromiseRef.current;
  }, [disclosureAccepted, runtime]);

  const processUserTurn = useCallback(
    async (
      rawText: string,
      source: "text" | "voice",
      options: TurnOptions = {},
    ) => {
      const text = rawText.trim();
      if (
        !text ||
        !disclosureAccepted ||
        transcriptOnly ||
        documentOperationBusy
      )
        return;
      // Capture before the first await, including session startup.
      const contextEpoch = contextEpochRef.current;
      const sessionEpoch = sessionEpochRef.current;
      const scope = captureTurnScope(
        options.architecture ?? architectureRef.current,
        options.diagramMode ?? askMode,
        options.responseIntent,
        options.architecture ? undefined : discussionDiagramRef.current,
        composerFocusRef.current,
      );
      const intent = ++turnIntentRef.current;
      const voiceEpoch = voiceEpochRef.current,
        voiceBridge = bridgeRef.current;
      if (turnQueueRef.current.sessionEpoch !== sessionEpoch) {
        // A new document must not wait for a request owned by the old session.
        turnQueueRef.current = {
          sessionEpoch,
          tail: Promise.resolve(),
          pending: 0,
        };
      }
      const queue = turnQueueRef.current,
        previousTurn = queue.tail,
        wasQueued = queue.pending > 0;
      let releaseTurn!: () => void;
      queue.tail = new Promise<void>((resolve) => {
        releaseTurn = resolve;
      });
      queue.pending += 1;
      let requestEpoch: number | undefined;
      const voiceCurrent = () =>
        source !== "voice" ||
        (voiceEpoch === voiceEpochRef.current &&
          voiceBridge === bridgeRef.current &&
          Boolean(voiceBridge?.connected));
      const clientRequestId = options.delegationId ?? crypto.randomUUID();
      setVoiceCaption("");
      finishCurrentOutput();
      if (source === "text") bridgeRef.current?.interrupt();
      setError("");
      setBusy(true);
      setStatus(`${profileName} is reviewing the canonical sources…`);
      if (!options.alreadyDisplayed) {
        addMessage({
          id: `user-${crypto.randomUUID()}`,
          role: "user",
          content: text,
        });
      }

      try {
        await previousTurn;
        if (
          sessionEpoch !== sessionEpochRef.current ||
          !voiceCurrent() ||
          ((source === "voice" || wasQueued) &&
            contextEpoch !== contextEpochRef.current)
        ) {
          if (
            intent === turnIntentRef.current &&
            sessionEpoch === sessionEpochRef.current
          )
            setStatus(
              "The queued question was cancelled because its session or architecture scope changed.",
            );
          return;
        }
        requestEpoch = ++requestEpochRef.current;
        const sessionId = await ensureAppSession();
        if (sessionEpoch !== sessionEpochRef.current) return;
        if (
          source === "voice" &&
          (contextEpoch !== contextEpochRef.current || !voiceCurrent())
        ) {
          if (requestEpoch === requestEpochRef.current)
            setStatus(
              "The earlier voice request was cancelled. Ask about the selected architecture.",
            );
          return;
        }
        const result = await runtime.turn({
          appSessionId: sessionId,
          clientRequestId,
          input: text,
          source,
          baseRevision: documentRef.current.revision,
          ...scope,
        });
        if (sessionEpoch !== sessionEpochRef.current) return;
        // A response can be committed while the user changes the selected view.
        // Reconcile its CAS revision, without moving or speaking in the new scope.
        if (result.document.revision > documentRef.current.revision) {
          documentRef.current = result.document;
          setDocumentState(result.document);
        }
        const present =
          requestEpoch === requestEpochRef.current &&
          intent === turnIntentRef.current &&
          contextEpoch === contextEpochRef.current &&
          voiceCurrent();
        if (result.mode === "skip") {
          if (present)
            setStatus(
              result.reason === "not-actionable"
                ? "Listening for a direct architecture question."
                : "Listening.",
            );
          return;
        }
        if (!result.turn)
          throw new Error("The backend returned no validated turn.");
        const diagrams =
          result.turn.documentPatch?.operations.flatMap((operation) =>
            operation.blocks.flatMap((block) =>
              block.type === "diagram" && block.format === "recipe"
                ? [block.spec as DiagramRecipe]
                : [],
            ),
          ) ?? [];
        if (diagrams.length && present) {
          setAnswerDiagrams((current) => [
            ...new Map(
              [...current, ...diagrams].map((diagram) => [diagram.id, diagram]),
            ).values(),
          ]);
          setActiveDiagramId(diagrams[0].id);
          setAskMode(diagrams[0].mode);
        }
        addMessage({
          id: result.turn.turnId,
          role: "assistant",
          content: result.turn.displayText,
          sources: result.turn.sources,
          evidenceLabel: result.turn.evidenceLabel,
          ...(scope.architecture
            ? {
                architectureScope: {
                  phase: scope.architecture.phase,
                  revision: scope.architecture.revision,
                },
              }
            : {}),
          diagrams,
          blocks: result.turn.documentPatch?.operations.flatMap(
            (operation) => operation.blocks,
          ),
        });
        if (present && scope.architecture) {
          setHighlightedEntityIds(sourceEntityIds(result.turn.sources));
        }
        if (present && source === "voice" && options.delegationId) {
          voiceBridge?.sendCommentary(
            options.delegationId,
            result.turn.spokenText,
          );
        }
        if (
          present &&
          result.turn.documentPatch?.focusSectionId &&
          !scope.architecture
        ) {
          window.setTimeout(() => {
            document
              .getElementById(result.turn?.documentPatch?.focusSectionId ?? "")
              ?.scrollIntoView({
                block: "start",
                behavior: window.matchMedia("(prefers-reduced-motion: reduce)")
                  .matches
                  ? "auto"
                  : "smooth",
              });
          }, 80);
        }
        if (requestEpoch === requestEpochRef.current)
          setStatus(
            present
              ? scope.architecture
                ? `Answer grounded in the ${scope.architecture.phase} architecture.`
                : "Answer added to the live document."
              : "The earlier answer was kept in your document. Your selected scope is unchanged.",
          );
      } catch (caught) {
        if (
          requestEpoch !== requestEpochRef.current ||
          intent !== turnIntentRef.current ||
          sessionEpoch !== sessionEpochRef.current ||
          contextEpoch !== contextEpochRef.current ||
          !voiceCurrent()
        )
          return;
        const message =
          caught instanceof Error
            ? caught.message
            : "The turn could not be completed.";
        setError(message);
        if (
          typeof caught === "object" &&
          caught !== null &&
          "code" in caught &&
          /^ARCHITECTURE_(?:STALE|DIGEST|DISABLED|SOURCE_DISABLED)/.test(
            String(caught.code),
          )
        ) {
          setArchitectureError(message);
          setHighlightedEntityIds([]);
          if (
            /^ARCHITECTURE_(?:DISABLED|SOURCE_DISABLED)/.test(
              String(caught.code),
            )
          ) {
            setArchitectureSnapshot(undefined);
            setArchitectureContext(undefined);
            snapshotRef.current = undefined;
            architectureRef.current = undefined;
          }
        }
        addMessage({
          id: `error-${crypto.randomUUID()}`,
          role: "assistant",
          content: `${message} No unvalidated content was added to the document.`,
          tone: "error",
        });
        if (source === "voice" && options.delegationId)
          voiceBridge?.sendCommentary(options.delegationId, SAFE_VOICE_FAILURE);
        setStatus("The answer could not be completed. You can retry.");
      } finally {
        queue.pending -= 1;
        if (
          queue === turnQueueRef.current &&
          sessionEpoch === sessionEpochRef.current
        )
          setBusy(queue.pending > 0);
        releaseTurn();
      }
    },
    [
      addMessage,
      disclosureAccepted,
      ensureAppSession,
      finishCurrentOutput,
      profileName,
      runtime,
      transcriptOnly,
      askMode,
      documentOperationBusy,
    ],
  );

  turnHandlerRef.current = processUserTurn;

  const realtimeCallbacks = useMemo(
    () => ({
      onStatus(message: string) {
        if (mountedRef.current) setStatus(message);
      },
      onParticipantText(text: string, messageId?: string) {
        updateVoiceCaption("user", text, messageId);
      },
      onAssistantText(text: string, messageId?: string) {
        updateVoiceCaption("assistant", text, messageId);
      },
      async onDelegation(text: string, delegationId: string) {
        if (!mountedRef.current) return;
        if (userCaptionIdRef.current)
          delegatedCaptionIdsRef.current.add(userCaptionIdRef.current);
        if (delegatedCaptionIdsRef.current.size > 128)
          delegatedCaptionIdsRef.current.delete(
            delegatedCaptionIdsRef.current.values().next().value!,
          );
        userCaptionIdRef.current = undefined;
        voiceCaptionAllowedRef.current = false;
        setVoiceCaption("");
        await turnHandlerRef.current?.(text, "voice", {
          alreadyDisplayed: true,
          delegationId,
        });
      },
      onInterrupted: () => {
        if (mountedRef.current) {
          finishCurrentOutput();
          setVoiceCaption("");
        }
      },
      onRemoteAudioAttached: () => {
        if (mountedRef.current) setRemoteAudioAttached(true);
      },
      onPlaybackComplete: () => {
        if (mountedRef.current) finishCurrentOutput();
      },
      onClosed: () => {
        if (!mountedRef.current) return;
        ++voiceEpochRef.current;
        setRemoteAudioAttached(false);
        setVoiceActive(false);
        setVoiceCaption("");
        voiceCaptionAllowedRef.current = false;
        void releaseRealtimeSession().catch(() =>
          setStatus(
            "The voice connection closed, but its server lease needs a session reset.",
          ),
        );
      },
    }),
    [updateVoiceCaption, finishCurrentOutput, releaseRealtimeSession],
  );

  async function startVoice() {
    if (
      !config ||
      !disclosureAccepted ||
      voiceActive ||
      voiceStartingRef.current ||
      documentOperationBusy
    )
      return;
    voiceStartingRef.current = true;
    setVoiceStarting(true);
    const sessionEpoch = sessionEpochRef.current;
    let bridge: RealtimeBridge | undefined;
    const current = () =>
      mountedRef.current &&
      sessionEpoch === sessionEpochRef.current &&
      bridgeRef.current === bridge;
    const callbacks: RealtimeCallbacks = {
      onStatus: (message) => {
        if (current()) realtimeCallbacks.onStatus(message);
      },
      onParticipantText: (text, id) => {
        if (current()) realtimeCallbacks.onParticipantText(text, id);
      },
      onAssistantText: (text, id) => {
        if (current()) realtimeCallbacks.onAssistantText(text, id);
      },
      onDelegation: async (text, id) => {
        if (current()) await realtimeCallbacks.onDelegation(text, id);
      },
      onInterrupted: () => {
        if (current()) realtimeCallbacks.onInterrupted();
      },
      onRemoteAudioAttached: () => {
        if (current()) realtimeCallbacks.onRemoteAudioAttached();
      },
      onPlaybackComplete: () => {
        if (current()) realtimeCallbacks.onPlaybackComplete();
      },
      onClosed: () => {
        if (current()) realtimeCallbacks.onClosed();
      },
    };
    setError("");
    try {
      const sessionId = await ensureAppSession();
      if (!mountedRef.current || sessionEpoch !== sessionEpochRef.current)
        return;
      bridge = config.testMode
        ? new FakeRealtimeBridge(callbacks)
        : new JurisRealtimeBridge(callbacks, runtime.createRealtimeSession);
      bridgeRef.current = bridge;
      setRemoteAudioAttached(false);
      await bridge.start(sessionId, config.disclosure, {
        name: config.profile,
        wakePhrase: config.wakePhrase,
      });
      if (!mountedRef.current || sessionEpoch !== sessionEpochRef.current) {
        await bridge.stop();
        return;
      }
      setVoiceActive(true);
    } catch (caught) {
      await bridge?.stop().catch(() => undefined);
      if (bridgeRef.current === bridge) bridgeRef.current = undefined;
      if (!mountedRef.current || sessionEpoch !== sessionEpochRef.current)
        return;
      await releaseRealtimeSession().catch(() => undefined);
      setError(
        caught instanceof Error
          ? caught.message
          : "The voice session could not start.",
      );
      setStatus("Voice is unavailable. Text chat remains available.");
      setVoiceActive(false);
    } finally {
      voiceStartingRef.current = false;
      if (mountedRef.current) setVoiceStarting(false);
    }
  }

  async function stopVoice() {
    ++voiceEpochRef.current;
    await bridgeRef.current?.stop();
    bridgeRef.current = undefined;
    await releaseRealtimeSession();
    setVoiceActive(false);
    setRemoteAudioAttached(false);
    setHighlightedEntityIds([]);
    setVoiceCaption("");
    voiceCaptionAllowedRef.current = false;
  }

  async function clearSession() {
    setCitedSources([]);
    setExplorerEpoch((epoch) => epoch + 1);
    focusedTabs.current.clear();
    composerFocusRef.current = undefined;
    activeTabRef.current = "explore";
    setWorkspaceOpen(false);
    setSummaryOpen(false);
    setOpenTabIds(["explore"]);
    setFocusLabel("");
    if (snapshotRef.current)
      changeArchitectureContext(createArchitectureContext(snapshotRef.current));
    requestEpochRef.current += 1;
    sessionEpochRef.current += 1;
    contextEpochRef.current += 1;
    discussionDiagramRef.current = undefined;
    setDiscussionDiagramId(undefined);
    setVoiceCaption("");
    sessionPromiseRef.current = undefined;
    finishCurrentOutput();
    await bridgeRef.current?.stop().catch(() => undefined);
    bridgeRef.current = undefined;
    await runtime.releaseSession().catch(() => undefined);
    appSessionIdRef.current = undefined;
    setAppSessionId(undefined);
    documentRef.current = EMPTY_DOCUMENT;
    setDocumentState(EMPTY_DOCUMENT);
    setMessages([]);
    setAnswerDiagrams([]);
    setActiveDiagramId(undefined);
    setDocumentSaved(false);
    setTranscriptOnly(false);
    setDocumentTitle("Untitled architecture document");
    setHighlightedEntityIds([]);
    setError("");
    setBusy(false);
    setVoiceActive(false);
    setRemoteAudioAttached(false);
    setStatus(
      "New document ready. Previously saved documents remain private in Juris.",
    );
  }

  async function openDocumentPicker() {
    setDocumentDialog("open");
    setDocumentOperationBusy(true);
    try {
      setSavedDocuments(await runtime.savedDocuments());
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Saved documents could not be loaded.",
      );
    } finally {
      setDocumentOperationBusy(false);
    }
  }

  function nameDocument(mode: "save" | "rename" | "copy") {
    setDocumentNameInput(
      mode === "copy" ? `${documentTitle} — copy` : documentTitle,
    );
    setDocumentDialog(mode);
  }

  async function completeDocumentAction() {
    if (!documentNameInput.trim() || documentOperationBusy) return;
    setDocumentOperationBusy(true);
    try {
      await ensureAppSession();
      const result =
        documentDialog === "copy"
          ? await runtime.copyDocument(
              documentNameInput,
              documentRef.current.revision,
            )
          : documentDialog === "rename"
            ? await runtime.renameDocument(documentNameInput)
            : await runtime.saveDocument(documentNameInput);
      if (documentDialog === "copy") {
        appSessionIdRef.current = result.conversationId ?? result.id;
        setAppSessionId(appSessionIdRef.current);
        if (result.document) {
          documentRef.current = result.document;
          setDocumentState(result.document);
        }
        setMessages([]);
      }
      setDocumentTitle(result.title ?? documentNameInput.trim());
      setDocumentSaved(result.persistence === "saved");
      setDocumentDialog(null);
      setStatus("Document saved privately in Juris.");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The document could not be saved.",
      );
    } finally {
      setDocumentOperationBusy(false);
    }
  }

  async function openSavedDocument(id: string) {
    setCitedSources([]);
    if (architectureRef.current)
      changeArchitectureContext({
        ...architectureRef.current,
        selectedEntityIds: [],
        selectedRelationshipIds: [],
      });
    setExplorerEpoch((epoch) => epoch + 1);
    focusedTabs.current.clear();
    composerFocusRef.current = undefined;
    activeTabRef.current = "explore";
    setOpenTabIds(["explore"]);
    setFocusLabel("");
    setWorkspaceOpen(false);
    setDocumentOperationBusy(true);
    try {
      await stopVoice();
      ++requestEpochRef.current;
      ++sessionEpochRef.current;
      ++contextEpochRef.current;
      discussionDiagramRef.current = undefined;
      setDiscussionDiagramId(undefined);
      sessionPromiseRef.current = undefined;
      setBusy(false);
      setStatus("Opening private document…");
      const result = await runtime.openDocument(id);
      appSessionIdRef.current = result.id;
      setAppSessionId(result.id);
      documentRef.current = result.document ?? EMPTY_DOCUMENT;
      setDocumentState(documentRef.current);
      setTranscriptOnly(!result.document);
      setDocumentTitle(result.title ?? "Saved document");
      setDocumentSaved(true);
      setAnswerDiagrams([]);
      setActiveDiagramId(undefined);
      setMessages(
        (result.messages ?? []).map((message) => ({
          ...message,
          diagrams: (
            (
              message as unknown as {
                blocks?: Array<{
                  type?: string;
                  format?: string;
                  spec?: DiagramRecipe;
                }>;
              }
            ).blocks ?? []
          ).flatMap((block) =>
            block.type === "diagram" && block.format === "recipe" && block.spec
              ? [block.spec]
              : [],
          ),
        })),
      );
      setDocumentDialog(null);
      setError("");
      setStatus(
        result.document
          ? "Private document restored at its saved revision."
          : "Older transcript restored. Start a new document to create diagrams.",
      );
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The document could not be opened.",
      );
    } finally {
      setDocumentOperationBusy(false);
    }
  }

  async function saveDiagram(diagram: DiagramRecipe) {
    if (busy || documentOperationBusy)
      throw new Error(
        "Wait for the current document update before saving this diagram.",
      );
    if (transcriptOnly)
      throw new Error(
        "Start a new document to save diagrams from this older transcript.",
      );
    setDocumentOperationBusy(true);
    try {
      const id = await ensureAppSession();
      const result = await runtime.updateDocument({
        appSessionId: id,
        clientRequestId: crypto.randomUUID(),
        baseRevision: documentRef.current.revision,
        operations: [
          {
            operation: "upsert_section",
            sectionId: `diagram-${diagram.id}`,
            title: diagram.title,
            blocks: [
              {
                type: "diagram",
                format: "recipe",
                spec: diagram as CompanionDiagramRecipe,
              },
            ],
          },
        ],
      });
      documentRef.current = result.document;
      setDocumentState(result.document);
      setStatus(
        documentSaved
          ? "Diagram saved to your private document."
          : "Diagram added. Save the document to keep it between sessions.",
      );
      if (!documentSaved) nameDocument("save");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The diagram could not be saved.",
      );
      throw caught;
    } finally {
      setDocumentOperationBusy(false);
    }
  }

  function exploreDiagram(diagram: DiagramRecipe) {
    setAnswerDiagrams((current) => [
      ...new Map(
        [...current, diagram].map((entry) => [entry.id, entry]),
      ).values(),
    ]);
    setOpenTabIds((ids) => [...new Set([...ids, diagram.id])]);
    activateTab(diagram.id);
    setWorkspaceOpen(true);
    setAskMode(diagram.mode);
  }

  function discussDiagram(diagram: DiagramRecipe) {
    if (
      !diagramMatchesSnapshot(diagram, snapshotRef.current) ||
      !architectureRef.current
    ) {
      setError(
        "This saved diagram uses an older catalogue revision. It remains readable; create and save an updated copy before discussing it.",
      );
      return;
    }
    if (!savedDiagrams.some((saved) => saved.id === diagram.id)) {
      setError(
        "Keep this diagram in your document before discussing its saved context.",
      );
      return;
    }
    const next = {
      ...architectureRef.current,
      phase: diagram.catalog.phase,
      filters: {},
      selectedEntityIds: diagram.nodes.flatMap((node) =>
        node.entityId ? [node.entityId] : [],
      ),
      selectedRelationshipIds: [],
    };
    changeArchitectureContext(next);
    discussionDiagramRef.current = diagram;
    setDiscussionDiagramId(diagram.id);
    setAskMode(diagram.mode);
    setStatus(
      `Discussing saved diagram in ${diagram.catalog.phase}, catalogue ${diagram.catalog.revision}.`,
    );
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const value = input;
    setInput("");
    void processUserTurn(value, "text");
  }

  useEffect(() => {
    mountedRef.current = true;
    const release = () => {
      ++requestEpochRef.current;
      ++sessionEpochRef.current;
      const bridge = bridgeRef.current;
      bridgeRef.current = undefined;
      void bridge?.stop().catch(() => undefined);
      void runtime.releaseSession();
    };
    window.addEventListener("pagehide", release);
    return () => {
      mountedRef.current = false;
      window.removeEventListener("pagehide", release);
      release();
    };
  }, [runtime]);

  useEffect(() => {
    if (!config?.testMode) return;
    window.__LIVE_DOCS_TEST__ = {
      async simulateVoice(text: string, delegationId?: string) {
        const bridge = bridgeRef.current;
        if (!(bridge instanceof FakeRealtimeBridge))
          throw new Error("The fake voice session is not active.");
        await bridge.simulateVoice(text, delegationId);
      },
      simulateCaption(role: "user" | "assistant", text: string, id?: string) {
        updateVoiceCaption(role, text, id);
      },
      simulateDisconnect() {
        const bridge = bridgeRef.current;
        if (bridge instanceof FakeRealtimeBridge) bridge.simulateDisconnect();
      },
      async expireSession() {
        if (!appSessionIdRef.current) return;
        await fetch("/api/test/expire", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ appSessionId: appSessionIdRef.current }),
        });
      },
      getState() {
        return {
          appSessionId: appSessionIdRef.current,
          revision: documentRef.current.revision,
          messages: messagesRef.current,
          lastSpokenText: bridgeRef.current?.lastSpokenText ?? "",
          voiceActive: bridgeRef.current?.connected ?? false,
        };
      },
    };
    return () => {
      delete window.__LIVE_DOCS_TEST__;
    };
  }, [config]);

  const documentExportButtons = (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="quiet-button"
        onClick={() =>
          downloadText(
            documentAsMarkdown(documentState, profileName),
            "text/markdown;charset=utf-8",
            `${config?.exportStem ?? "live-document"}.md`,
          )
        }
        disabled={!documentState.sections.length}
      >
        <FileText aria-hidden="true" />
        <span className="action-label">Markdown</span>
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="quiet-button"
        onClick={() =>
          downloadText(
            `${JSON.stringify(documentState, null, 2)}\n`,
            "application/json",
            `${config?.exportStem ?? "live-document"}.json`,
          )
        }
        disabled={!documentState.sections.length}
      >
        <FileJson aria-hidden="true" />
        <span className="action-label">JSON</span>
      </Button>
    </>
  );

  const workspaceHeader = (
    <div className="chat-topbar">
      <h1 id="document-title">{documentTitle}</h1>
      <div role="group" aria-label="Architecture phase">
        {(["target", "current"] as const).map((phase) => (
          <Button
            key={phase}
            variant="ghost"
            size="sm"
            aria-pressed={architectureContext?.phase === phase}
            onClick={() => {
              if (architectureContext) {
                changeArchitectureContext({
                  ...architectureContext,
                  phase,
                  selectedEntityIds: [],
                  selectedRelationshipIds: [],
                });
                focusedTabs.current.clear();
                composerFocusRef.current = undefined;
                setFocusLabel("");
              }
            }}
          >
            {phase === "target" ? "Target" : "Current"}
          </Button>
        ))}
      </div>
      <Button
        variant="ghost"
        size="sm"
        aria-pressed={summaryOpen}
        onClick={() => setSummaryOpen(!summaryOpen)}
      >
        Summary
      </Button>
      <Button
        variant="ghost"
        size="sm"
        aria-pressed={workspaceOpen}
        onClick={() => setWorkspaceOpen(!workspaceOpen)}
      >
        Workspace
      </Button>
      <details className="file-menu">
        <summary>More</summary>
        <div>
          {!config?.testMode && <AppLauncher targets={launchTargets} currentApp="live-docs" className="juris-launcher" />}
          <Button variant="ghost" onClick={() => void openDocumentPicker()}>
            Open
          </Button>
          <Button
            variant="ghost"
            disabled={!disclosureAccepted || busy || transcriptOnly}
            onClick={() => nameDocument(documentSaved ? "rename" : "save")}
          >
            {documentSaved ? "Rename" : "Save"}
          </Button>
          {documentSaved && (
            <Button variant="ghost" onClick={() => nameDocument("copy")}>
              Save as
            </Button>
          )}
          {documentExportButtons}
        </div>
      </details>
      {account && (
        <AppUserMenu
          displayName={account.name}
          email={account.email}
          onNavigateProfile={() => setAccountOpen(true)}
          onSignOut={async () => {
            await stopVoice();
            await runtime.releaseSession();
            await account.onSignOut();
          }}
        />
      )}
    </div>
  );

  return (
    <TooltipProvider delayDuration={250}>
      <div className="live-docs-theme">
        <AppShell
          className="live-docs-shell chat-first-shell"
          mainClassName="live-docs-main"
          defaultOpen={true}
          mobileBrand={<span className="product-name">Live Docs</span>}
          sidebar={
            <LiveDocsNavigation
              onNew={() => void clearSession()}
              onSaved={() => void openDocumentPicker()}
              onExplore={() => {
                setOpenTabIds((ids) => [...new Set([...ids, "explore"])]);
                activateTab("explore");
                setWorkspaceOpen(true);
              }}
            />
          }
        >
          <ConversationWorkspace
            header={workspaceHeader}
            splitTabId={splitTabId}
            onSplitTabChange={setSplitTabId}
            showControls={false}
            className="live-docs-conversation"
            workspaceOpen={workspaceOpen}
            onWorkspaceOpenChange={setWorkspaceOpen}
            summaryOpen={summaryOpen}
            onSummaryOpenChange={setSummaryOpen}
            activeTabId={activeDiagramId ?? "explore"}
            onActiveTabChange={activateTab}
            onCloseTab={(id) => {
              setOpenTabIds((ids) => ids.filter((entry) => entry !== id));
              activateTab("explore");
            }}
            summary={
              <section className="pinned-summary">
                <h2>Session summary</h2>
                {documentState.sections.length ? (
                  documentState.sections.slice(-5).map((section) => (
                    <p key={section.sectionId}>
                      <strong>{section.title}</strong> ·{" "}
                      {section.blocks
                        .flatMap((block) =>
                          block.type === "markdown"
                            ? [
                                block.markdown
                                  .replace(/[#*`]/g, "")
                                  .split(/\n|(?<=[.!?])\s/)[0]
                                  .slice(0, 180),
                              ]
                            : [],
                        )
                        .slice(0, 1)
                        .join("")}
                    </p>
                  ))
                ) : (
                  <p>
                    Accepted notes and decisions will appear here as the
                    conversation develops.
                  </p>
                )}
              </section>
            }
            tabs={openTabIds.map((id) => ({
              id,
              title:
                id === "explore"
                  ? "Architecture"
                  : id === "document"
                    ? "Working document"
                    : id === "sources"
                      ? "Sources"
                      : (allDiagrams.find((diagram) => diagram.id === id)
                          ?.title ?? "Diagram"),
              closable: true,
              content:
                id === "document" ? (
                  <div className="document-content" data-testid="live-document">
                    {documentState.sections.length === 0 ? (
                      <div className="document-empty">
                        <Sparkles aria-hidden="true" />
                        <h2>Your shared explanation will build here</h2>
                        <p>
                          Ask {profileName} about a platform decision. Answers
                          become a cumulative document with concise notes,
                          evidence labels, and diagrams when a picture makes the
                          flow clearer.
                        </p>
                        <div
                          className="empty-flow"
                          aria-label="Live Docs process"
                        >
                          <span>Ask</span>
                          <i aria-hidden="true" />
                          <span>Explain</span>
                          <i aria-hidden="true" />
                          <span>Draw</span>
                        </div>
                      </div>
                    ) : (
                      documentState.sections.map((section) => (
                        <article
                          className="document-section"
                          id={section.sectionId}
                          key={section.sectionId}
                          data-testid={`section-${section.sectionId}`}
                        >
                          <div className="section-kicker">
                            Updated in {section.updatedByTurnId}
                          </div>
                          <h2>{section.title}</h2>
                          {section.blocks.map((block, index) =>
                            block.type === "markdown" ? (
                              <MarkdownBlock
                                key={`${section.updatedByTurnId}-${index}`}
                              >
                                {block.markdown}
                              </MarkdownBlock>
                            ) : block.format === "mermaid" ? (
                              <ProgressiveMermaid
                                key={`${section.updatedByTurnId}-${index}`}
                                title={block.title}
                                description={block.description}
                                source={block.source}
                                completeToken={completeRevealToken}
                              />
                            ) : (
                              <RecipeDiagram
                                key={`${section.updatedByTurnId}-${index}`}
                                diagram={block.spec as DiagramRecipe}
                                onExplore={exploreDiagram}
                                onSave={saveDiagram}
                              />
                            ),
                          )}
                        </article>
                      ))
                    )}
                  </div>
                ) : id === "sources" ? (
                  <section className="sources-workspace">
                    <h2>Sources</h2>
                    {citedSources?.map((citation, index) => (
                      <details key={`${citation.id}-${index}`} open>
                        <summary>{citation.title}</summary>
                        <p>{citation.excerpt}</p>
                        {typeof citation.metadata?.sourceId === "string" &&
                          architectureSnapshot?.catalog.sources.some(
                            (source) =>
                              source.id === citation.metadata?.sourceId &&
                              source.enabled !== false,
                          ) && (
                            <a href={`#source-${citation.metadata.sourceId}`}>
                              View source excerpts
                            </a>
                          )}
                      </details>
                    ))}
                    {architectureSnapshot?.catalog.sources
                      .filter((source) => source.enabled !== false)
                      .map((source) => (
                        <details key={source.id} id={`source-${source.id}`}>
                          <summary>{source.title}</summary>
                          <p>
                            {source.status} · {source.asOf}
                          </p>
                          {architectureSnapshot.catalog.reviewedContent?.excerpts
                            .filter((excerpt) => excerpt.sourceId === source.id)
                            .map((excerpt) => (
                              <article key={excerpt.id} id={excerpt.id}>
                                <h3>
                                  {excerpt.title}
                                  {excerpt.slide
                                    ? ` · slide ${excerpt.slide}`
                                    : ""}
                                </h3>
                                <p>{excerpt.text}</p>
                              </article>
                            ))}
                        </details>
                      ))}
                  </section>
                ) : architectureSnapshot && architectureContext ? (
                  <DiagramExplorer
                    key={`${explorerEpoch}:${id}`}
                    enabled={
                      workspaceOpen && (activeDiagramId ?? "explore") === id
                    }
                    snapshot={architectureSnapshot}
                    context={architectureContext}
                    initialDiagram={allDiagrams.find(
                      (diagram) => diagram.id === id,
                    )}
                    onGenerate={async (request) => {
                      const epoch = contextEpochRef.current;
                      const diagram = await runtime.generateDiagram(
                        structuredClone(request),
                      );
                      if (epoch !== contextEpochRef.current)
                        throw new Error(
                          "The architecture phase changed. Open the diagram again.",
                        );
                      return diagram;
                    }}
                    onFocus={(focus, label, kind, displayLevel) => {
                      focusedTabs.current.set(id, { focus, label, kind, displayLevel });
                      if (activeTabRef.current !== id) return;
                      composerFocusRef.current =
                        focus.entityId || focus.boundaryId || focus.flowId || displayLevel
                          ? { focus, kind, displayLevel }
                          : undefined;
                      setFocusLabel(label === "Overview" ? "" : label);
                      changeArchitectureContext({
                        ...architectureContext,
                        selectedEntityIds: focus.entityId
                          ? [focus.entityId]
                          : [],
                        selectedRelationshipIds: [],
                      });
                    }}
                    onSave={saveDiagram}
                    onDiscuss={discussDiagram}
                    onCompare={(diagram) =>
                      exploreDiagram({
                        ...diagram,
                        id: diagram.id + "-comparison-" + Date.now(),
                      })
                    }
                  />
                ) : (
                  <p role="status">
                    {architectureError || "Loading architecture…"}
                  </p>
                ),
            }))}
            composer={
              <div className="chat-composer-region">
                {voiceCaption && (
                  <p className="voice-caption" data-testid="voice-caption">
                    {voiceCaption}
                  </p>
                )}
                {discussionDiagramId && discussionDiagramRef.current && (
                  <p data-testid="discussion-scope">
                    Discussing saved diagram ·{" "}
                    {discussionDiagramRef.current.catalog.phase} · revision{" "}
                    {discussionDiagramRef.current.catalog.revision}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        ++contextEpochRef.current;
                        bridgeRef.current?.interrupt();
                        discussionDiagramRef.current = undefined;
                        setDiscussionDiagramId(undefined);
                      }}
                    >
                      End diagram discussion
                    </Button>
                  </p>
                )}{" "}
                <form className="composer" onSubmit={submit}>
                  {focusLabel && (
                    <p className="focus-chip">Focus · {focusLabel}</p>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    data-testid="voice-control"
                    aria-pressed={voiceActive}
                    onClick={() =>
                      void (voiceActive ? stopVoice() : startVoice())
                    }
                    disabled={
                      !disclosureAccepted ||
                      Boolean(configurationError) ||
                      voiceStarting ||
                      documentOperationBusy
                    }
                  >
                    {voiceActive ? <MicOff /> : <Mic />}
                    {voiceStarting
                      ? "Starting voice…"
                      : voiceActive
                        ? "End voice"
                        : "Start voice"}
                  </Button>
                  <div
                    className="diagram-answer-mode"
                    role="group"
                    aria-label="Answer evidence mode"
                  >
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-pressed={askMode === "documented"}
                      onClick={() => setAskMode("documented")}
                    >
                      Documented
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-pressed={askMode === "what-if"}
                      onClick={() => setAskMode("what-if")}
                    >
                      What-if proposal
                    </Button>
                  </div>
                  <Label htmlFor="question" className="sr-only">
                    Ask {profileName}
                  </Label>
                  <Textarea
                    id="question"
                    data-testid="question-input"
                    className="composer-input"
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                      }
                    }}
                    placeholder={`Ask ${profileName} about the architecture…`}
                    rows={2}
                    maxLength={5_000}
                    disabled={
                      !disclosureAccepted ||
                      Boolean(configurationError) ||
                      transcriptOnly ||
                      documentOperationBusy
                    }
                  />
                  <Button
                    type="submit"
                    size="icon"
                    className="send-button"
                    disabled={
                      !input.trim() ||
                      !disclosureAccepted ||
                      busy ||
                      documentOperationBusy
                    }
                    aria-label="Send question"
                  >
                    <ArrowUp aria-hidden="true" />
                  </Button>
                </form>
                <p className="memory-note">
                  {documentSaved
                    ? "Saved privately. Accepted changes save automatically."
                    : "Private draft · Save to keep this conversation."}
                </p>
              </div>
            }
          >
            <div className="chat-transcript">
              <div
                className="status-line"
                role="status"
                aria-live="polite"
                data-busy={busy}
                data-testid="turn-status"
              >
                {status}
              </div>
              {error && (
                <Alert variant="destructive" data-testid="error-banner">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}{" "}
              <ol
                className="message-list"
                tabIndex={0}
                aria-live="polite"
                aria-label="Conversation"
              >
                {messages.length === 0 ? (
                  <li className="conversation-empty">
                    <Volume2 aria-hidden="true" />
                    <p>
                      Ask in writing, or start voice and say “{wakePhrase}.”
                    </p>
                  </li>
                ) : (
                  messages.map((message) => (
                    <li key={message.id}>
                      {message.architectureScope && (
                        <p className="answer-scope">
                          {message.architectureScope.phase} · catalogue{" "}
                          {message.architectureScope.revision}
                        </p>
                      )}
                      <CompanionMessageItem
                        message={message}
                        variant="rail"
                        assistantLabel={profileName}
                        sourceDisplay="collapsible"
                        sourceLinksEnabled={false}
                        className="message"
                        renderDocumentBlock={(block, index) =>
                          block.type === "diagram" &&
                          block.format === "recipe" ? (
                            <Button
                              variant="outline"
                              key={index}
                              className="diagram-attachment"
                              onClick={() =>
                                exploreDiagram(block.spec as DiagramRecipe)
                              }
                            >
                              <Network aria-hidden="true" />
                              <span>
                                {(block.spec as DiagramRecipe).title}
                                <small>
                                  {(block.spec as DiagramRecipe).kind} ·{" "}
                                  {(block.spec as DiagramRecipe).catalog.phase}
                                </small>
                              </span>
                              <span>Open diagram ↗</span>
                            </Button>
                          ) : null
                        }
                      />
                      {sourceEntityIds(message.sources).length ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="architecture-show-source"
                          onClick={() =>
                            showArchitectureSources(message.sources)
                          }
                        >
                          Show sources
                        </Button>
                      ) : null}
                    </li>
                  ))
                )}
              </ol>
              {messages.length === 0 && (
                <div className="suggestions" aria-label="Suggested questions">
                  {(config?.starterPrompts ?? []).map((prompt) => (
                    <Button
                      key={prompt}
                      variant="outline"
                      size="sm"
                      onClick={() => void processUserTurn(prompt, "text")}
                      disabled={!disclosureAccepted || busy}
                    >
                      {prompt}
                    </Button>
                  ))}
                </div>
              )}
              <div className="chat-context-actions">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setOpenTabIds((ids) => [...new Set([...ids, "document"])]);
                    activateTab("document");
                    setWorkspaceOpen(true);
                  }}
                >
                  Open working document
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setOpenTabIds((ids) => [...new Set([...ids, "sources"])]);
                    activateTab("sources");
                    setWorkspaceOpen(true);
                  }}
                >
                  Sources
                </Button>
              </div>
              <span
                className="sr-only"
                data-testid="remote-audio-state"
                data-attached={remoteAudioAttached}
              >
                Remote audio {remoteAudioAttached ? "attached" : "not attached"}
              </span>
              <span className="sr-only" data-testid="document-revision">
                Revision {documentState.revision}
              </span>
            </div>
          </ConversationWorkspace>
        </AppShell>

        <Dialog
          open={documentDialog !== null}
          onOpenChange={(open) => {
            if (!open && !documentOperationBusy) setDocumentDialog(null);
          }}
        >
          <DialogContent className="document-dialog">
            <DialogHeader>
              <DialogTitle>
                {documentDialog === "open"
                  ? "Open a private document"
                  : documentDialog === "rename"
                    ? "Rename document"
                    : documentDialog === "copy"
                      ? "Save a private copy"
                      : "Save private document"}
              </DialogTitle>
              <DialogDescription>
                {documentDialog === "open"
                  ? "Only documents saved by your account are listed."
                  : "Diagrams retain their original sources, phase, and catalogue revision."}
              </DialogDescription>
            </DialogHeader>
            {documentDialog === "open" ? (
              <div className="saved-document-list">
                {documentOperationBusy ? (
                  <p role="status">Loading documents…</p>
                ) : savedDocuments.length ? (
                  savedDocuments.map((item) => (
                    <Button
                      key={item.id}
                      variant="ghost"
                      onClick={() => void openSavedDocument(item.id)}
                      disabled={documentOperationBusy}
                    >
                      <span>
                        {item.title || "Untitled document"}
                        <small>
                          {item.documentRevision !== undefined
                            ? `Revision ${item.documentRevision}`
                            : "Saved conversation"}
                        </small>
                      </span>
                    </Button>
                  ))
                ) : (
                  <p>No saved documents yet.</p>
                )}
              </div>
            ) : (
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  void completeDocumentAction();
                }}
              >
                <Label htmlFor="document-name">Document name</Label>
                <Input
                  id="document-name"
                  value={documentNameInput}
                  onChange={(event) => setDocumentNameInput(event.target.value)}
                  maxLength={160}
                  autoFocus
                />
                <Button
                  type="submit"
                  disabled={documentOperationBusy || !documentNameInput.trim()}
                >
                  {documentOperationBusy ? "Saving…" : "Save"}
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={accountOpen} onOpenChange={setAccountOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Juris account</DialogTitle>
              <DialogDescription>{account?.name}</DialogDescription>
            </DialogHeader>
            <p>{account?.email}</p>
            <p>
              Your ICC identity and application access are managed centrally in
              Juris.
            </p>
          </DialogContent>
        </Dialog>

        <Dialog open={!disclosureAccepted}>
          <DialogContent
            className="disclosure-dialog [&>button]:hidden"
            data-testid="disclosure-dialog"
            onEscapeKeyDown={(event) => event.preventDefault()}
            onPointerDownOutside={(event) => event.preventDefault()}
          >
            <div className="disclosure-content">
              <div className="disclosure-icon" aria-hidden="true">
                <Sparkles />
              </div>
              <DialogHeader className="disclosure-heading">
                <p className="eyebrow">Before we begin</p>
                <DialogTitle>Meet {profileName}</DialogTitle>
                <DialogDescription>
                  {config?.disclosure ??
                    (configurationError || "Loading the required disclosure…")}
                </DialogDescription>
              </DialogHeader>
              <div className="consent-row">
                <Checkbox
                  id="disclosure-consent"
                  ref={consentRef}
                  checked={disclosureChecked}
                  onCheckedChange={(checked) =>
                    setDisclosureChecked(checked === true)
                  }
                  disabled={!config}
                />
                <Label htmlFor="disclosure-consent">
                  I understand that {profileName} is an AI using a synthetic
                  voice and that this conversation may be processed by approved
                  service providers.
                </Label>
              </div>
              <Button
                type="button"
                className="primary-button"
                disabled={!disclosureChecked || !config}
                onClick={() => setDisclosureAccepted(true)}
              >
                Continue to Live Docs
              </Button>
              <p className="disclosure-footnote">
                Microphone access and provider sessions stay blocked until you
                continue.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

declare global {
  interface Window {
    __LIVE_DOCS_TEST__?: {
      simulateVoice(text: string, delegationId?: string): Promise<void>;
      simulateCaption(
        role: "user" | "assistant",
        text: string,
        id?: string,
      ): void;
      simulateDisconnect(): void;
      expireSession(): Promise<void>;
      getState(): {
        appSessionId?: string;
        revision: number;
        messages: ConversationMessage[];
        lastSpokenText: string;
        voiceActive: boolean;
      };
    };
  }
}
